import { Link } from "react-router-dom";

const Terms = () => {
  return (
    <div data-testid="terms-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-primary/5 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary">
              Terms & Conditions
            </h1>
            <p className="text-slate-600 mt-4">
              Last updated: December 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 mb-8">
              Welcome to CartNexa. By accessing or using our website and services, you agree to be bound by these Terms and Conditions. Please read them carefully before using our services.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              1. Website Usage Rules
            </h2>
            <p className="text-slate-600 mb-4">
              By using the CartNexa website, you agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Use the website for lawful purposes only</li>
              <li>Provide accurate and complete information when filling out forms</li>
              <li>Not interfere with the website's functionality or security</li>
              <li>Not attempt to gain unauthorized access to our systems</li>
              <li>Not use automated systems to access the website without permission</li>
              <li>Respect our intellectual property rights</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              2. Service Terms
            </h2>
            <h3 className="font-heading text-xl font-bold text-secondary mt-8 mb-3">
              2.1 Service Description
            </h3>
            <p className="text-slate-600 mb-4">
              CartNexa provides residential and commercial cleaning services in the Atlanta metropolitan area. Services include but are not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>Standard house cleaning</li>
              <li>Deep cleaning</li>
              <li>Move-in/Move-out cleaning</li>
              <li>Office cleaning</li>
              <li>Recurring cleaning services</li>
            </ul>

            <h3 className="font-heading text-xl font-bold text-secondary mt-8 mb-3">
              2.2 Service Booking
            </h3>
            <p className="text-slate-600 mb-4">
              When booking a cleaning service:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>You must provide accurate information about your property and requirements</li>
              <li>You must ensure safe access to the property at the scheduled time</li>
              <li>You must secure or remove valuable and fragile items</li>
              <li>You must inform us of any pets on the premises</li>
              <li>You must disclose any hazardous conditions or materials</li>
            </ul>

            <h3 className="font-heading text-xl font-bold text-secondary mt-8 mb-3">
              2.3 Service Guarantee
            </h3>
            <p className="text-slate-600 mb-6">
              We offer a 100% satisfaction guarantee. If you are not satisfied with our cleaning service, please contact us within 24 hours of service completion, and we will re-clean the specified areas at no additional cost.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              3. Payment Terms
            </h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li><strong>Pricing:</strong> All prices are quoted in US dollars and are subject to change. Final pricing is provided after assessment of the property.</li>
              <li><strong>Payment Methods:</strong> We accept major credit cards, debit cards, and electronic payments.</li>
              <li><strong>Payment Timing:</strong> Payment is due upon completion of service unless otherwise arranged.</li>
              <li><strong>Additional Charges:</strong> Additional charges may apply for excessive mess, biohazard cleanup, or services outside the original scope.</li>
              <li><strong>Recurring Services:</strong> For recurring services, payment will be processed according to the agreed schedule.</li>
            </ul>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              4. Cancellation Policy
            </h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li><strong>24+ Hours Notice:</strong> Free cancellation or rescheduling</li>
              <li><strong>Less than 24 Hours:</strong> 50% of the service fee may be charged</li>
              <li><strong>No Show / Same Day:</strong> Full service fee may be charged</li>
              <li><strong>Recurring Services:</strong> Cancel or pause recurring services with 48 hours notice</li>
            </ul>
            <p className="text-slate-600 mb-6">
              To cancel or reschedule, please call us at +1-871-7871311 or email support@cartnexa.store.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              5. Liability Limitations
            </h2>
            <h3 className="font-heading text-xl font-bold text-secondary mt-8 mb-3">
              5.1 General Liability
            </h3>
            <p className="text-slate-600 mb-4">
              While we take great care in providing our services:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li>We are not liable for damage to items not disclosed or properly secured</li>
              <li>We are not liable for pre-existing damage or wear</li>
              <li>We are not liable for damage caused by defective items or surfaces</li>
              <li>Our liability is limited to the cost of the service provided</li>
            </ul>

            <h3 className="font-heading text-xl font-bold text-secondary mt-8 mb-3">
              5.2 Insurance
            </h3>
            <p className="text-slate-600 mb-6">
              CartNexa maintains comprehensive general liability insurance. In the event of damage caused by our staff during service, please report it within 24 hours for review and resolution.
            </p>

            <h3 className="font-heading text-xl font-bold text-secondary mt-8 mb-3">
              5.3 Website Disclaimer
            </h3>
            <p className="text-slate-600 mb-6">
              The CartNexa website is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free website operation.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              6. Intellectual Property
            </h2>
            <p className="text-slate-600 mb-6">
              All content on the CartNexa website, including text, graphics, logos, images, and software, is the property of CartNexa and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              7. Privacy
            </h2>
            <p className="text-slate-600 mb-6">
              Your use of our services is also governed by our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>, which describes how we collect, use, and protect your personal information.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              8. Dispute Resolution
            </h2>
            <p className="text-slate-600 mb-6">
              Any disputes arising from these Terms or our services will be resolved through good-faith negotiation. If a resolution cannot be reached, disputes will be subject to binding arbitration in accordance with Georgia state law.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              9. Changes to Terms
            </h2>
            <p className="text-slate-600 mb-6">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of our services constitutes acceptance of the modified Terms.
            </p>

            <h2 className="font-heading text-2xl font-bold text-secondary mt-12 mb-4">
              10. Contact Information
            </h2>
            <p className="text-slate-600 mb-4">
              For questions about these Terms, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <p className="text-slate-700 font-medium">CartNexa</p>
              <p className="text-slate-600">783 Meadowbrook Lane</p>
              <p className="text-slate-600">Atlanta, GA 30303</p>
              <p className="text-slate-600 mt-2">
                Email: <a href="mailto:support@cartnexa.store" className="text-primary hover:underline">support@cartnexa.store</a>
              </p>
              <p className="text-slate-600">
                Phone: <a href="tel:+18717871311" className="text-primary hover:underline">+1-871-7871311</a>
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-200">
              <Link to="/" className="text-primary font-medium hover:underline">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
